//------------------------------------------------------------------------------
// BFA10103G 共架申込 機器設備
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';

// 共通
import withRouter from '@/components/organisms/withRouter';
import AppLayout from '@/layouts/app-layout';
import PageTitle from '@/components/molecules/PageTitle';
import MessageArea from '@/components/molecules/WarningMessageArea';
import CustomStepper from '@/components/molecules/Stepper';
import commonOperations from '@/reducks/common/operations';
import backend from '@/common/backend';
import PoleItem from '@/utils/pole-item';
import { makeDataMap } from '@/utils/wijmo-utils';

// 共架申込
import MainPole from '@/pages/bfa/common/main-pole';
import TitledPanel from '@/components/titled-panel';
import OrderInformations from '@/pages/bfa/common/order-info';
import OrderNumber from '@/pages/bfa/common/order-number';
import SetubiMapArea from './map';
import OperationPanel from './ope-panel';
import SetubiListGrid from './setubi-list';
import SetubiDialog from './setubi-dialog';

// 定数
const PAGE_NAME = '共架申込 機器設備';

/**
 * 共架申込 機器設備
 */
class OrderSetubi extends React.Component {
  /**
   * コンストラクタ
   */
  constructor(props) {
    super(props);

    // ステート
    this.state = {
      warnings: [],       // 警告メッセージ
      orderInfo: {},      // 申込情報
      mainPole: null,     // 代表電柱
      setubiList: [],     // 機器設備一覧
      devCodeMap: null,   // 架設設備コードマップ
      nearDevMap: null,   // 最接近配電設備コードマップ
      idoTypeMap: null,   // 異動種別マップ

      // 設備一覧
      setubiSelected: null,   // 選択(行クリック)した設備
      setubiDialog: null,     // 編集ダイアログ表示設定
    };

    // 設備一覧グリッド参照
    this.gridRef = React.createRef();
  }

  /**
   * マウント
   */
  async componentDidMount() {

    // 申込番号 (パスパラメーター)
    const orderNo = OrderNumber.from(this.props.params);

    // 申込取得
    const res = await backend.get('/bfa10103g/load/' + orderNo.path());
    if (res.parse(this.props.doShowMessage).ok) {
      this.parseResponse(res.body);
    }
  }

  // レスポンス解析
  parseResponse = (body) => {

    // 設備一覧にレコードIDを付加
    let recordId = 0;
    const setubiList = (body.setubiList || []).map((data) => ({
      ...data,
    recordId: recordId++,
      poleDisp: PoleItem.text(data),   // 表示用電柱番号も追加
    }));

    // 申込情報更新
    this.setState({
      orderInfo:  body.orderInfo ?? {},
      mainPole:   MainPole.of(body.orderInfo),
      setubiList: setubiList,
      devCodeMap: makeDataMap(body.devCodeList),
      nearDevMap: makeDataMap(body.nearDevList),
      idoTypeMap: makeDataMap(body.idoTypeList),
    });

    // 設備一覧更新
    this.gridRef.current?.setItemsSource(setubiList);
  };

  /**
   * 描画
   */
  render() {
    const { genericMap } = this.props;
    const orderNo = OrderNumber.from(this.props.params);
    const orderInfo = this.state.orderInfo;

    // 保存可否
    const orderStatus = orderInfo.orderStatus;
    const savable = (orderStatus === '01');  // 作成中

    // 描画
    return (
      <AppLayout>

        {/* ページタイトル */}
        <PageTitle pageTitle={PAGE_NAME} />

        {/* メッセージ領域 */}
        <MessageArea messages={this.state.warnings}/>

        {/* 申込進捗 */}
        <Box sx={{ mt: 1 }}>
          <CustomStepper
            category="1"
            status={orderInfo.orderStatus}
            genericStatus={genericMap?.order_status ?? []}
          />
        </Box>

        {/* 申込情報 */}
        <OrderInformations
          orderNo={orderNo}
          orderInfo={orderInfo}
        />

        {/* 地図エリア */}
        <TitledPanel title="地図" sx={{ mt: 2 }}>
          <SetubiMapArea
            mainPole={this.state.mainPole}
            setubiList={this.state.setubiList}
            setubiSelected={this.state.setubiSelected}
            onSetubiAdd={this.handleSetubiAdd}
            onSetubiEdit={this.handleSetubiEdit}
            onSetubiDelete={this.handleSetubiDelete}
          />
        </TitledPanel>

        {/* 設備一覧 */}
        <TitledPanel title="機器設備" sx={{ mt: 2 }}>
          <SetubiListGrid
            gridRef={this.gridRef}
            devCodeMap={this.state.devCodeMap}
            idoTypeMap={this.state.idoTypeMap}
            nearDevMap={this.state.nearDevMap}
            isReadOnly={!savable}
            onItemEdit={this.handleSetubiEdit}
            onItemSelect={this.handleSetubiSelect}
            onItemUpdate={this.handleSetubiUpdate}
          />
        </TitledPanel>

        {/* 操作パネル */}
        <OperationPanel
          orderNo={orderNo}
          savable={savable}
          navigate={this.props.navigate}
          onSave={this.handleSave}
        />

        {/* 設備編集ダイアログ */}
        <SetubiDialog
          initValues={this.state.setubiDialog}
          devCodeMap={this.state.devCodeMap}
          nearDevMap={this.state.nearDevMap}
          onDialogClose={this.handleDialogClose}
        />
      </AppLayout>
    );
  }

  /** 一時保存 */
  handleSave = async () => {
    console.info('一時保存');
    const orderNo = OrderNumber.from(this.props.params);
    const lastUpdate = this.state.orderInfo.updateTime;
    const setubiList = this.state.setubiList?.map((setubi) => ({
      bukkenNo:     setubi.bukkenNo,       // 物件番号
      areaCode:     setubi.areaCode,       // 地区コード
      lineCode:     setubi.lineCode,       // 線路コード
      poleNo:       setubi.poleNo,         // 電柱番号
      setubiCode:   setubi.setubiCode,     // 架設設備コード
      idoType:      setubi.idoType,        // 異動種別
      tijyoHeight:  setubi.tijyoHeight,    // 地上高
      nearSetubi:   setubi.nearSetubi,     // 最接近配電設備
      rikakuHaiden: setubi.rikakuHaiden,   // 配電設備との離隔
      rikakuUpper:  setubi.rikakuUpper,    // 通信線との離隔/上位
      rikakuLower:  setubi.rikakuLower,    // 通信線との離隔/下位
    }));

    // 設備情報保存
    const req = { setubiList, lastUpdate };
    const res = await backend.post('/bfa10103g/save/' + orderNo.path(), req);
    if (res.parse(this.props.doShowMessage).ok) {
      this.parseResponse(res.body);
    }
  }

  /**
   * 設備追加
   */
  handleSetubiAdd = (pole) => {
    const view = this.gridRef.current?.getView();
    if (view) {
      console.info('設備追加', pole);

      // レコードID/物件番号の最大値
      const orgItems = view.sourceCollection;
      const max = orgItems.reduce((ret, item) => ({
        recordId: Math.max(ret.recordId, item.recordId),
        bukkenNo: Math.max(ret.bukkenNo, item.bukkenNo || 0),
      }), { recordId: 0, bukkenNo: 0 });

      // 新しい項目を作成
      const newItem = {
        ...pole,
        recordId:  max.recordId + 1,
        bukkenNo:  max.bukkenNo + 1,
        poleDisp: PoleItem.text(pole),
        idoType:  '1',
      };

      // 一覧に追加
      orgItems.push(newItem);
      view.refresh();
    }
  };

  /** 設備編集 */
  handleSetubiEdit = (setubi) => {
    console.info('設備編集', setubi);

    // 設備編集ダイアログ表示
    this.setState({ setubiDialog: setubi });
  };

  /** 設備編集ダイアログ終了 */
  handleDialogClose = (result) => {
    this.setState({ setubiDialog: null });

    // キャンセル終了
    if (!result) {
      return;
    }

    // 設備一覧の該当項目を検索
    const view = this.gridRef.current?.getView();
    const item = view && view.sourceCollection.find(
        (source) => source.recordId === result.recordId);

    // 設備一覧に編集結果を反映
    if (item) {
      view.editItem(item);
      item.setubiCode   = result.setubiCode;
      item.nearSetubi   = result.nearSetubi;
      item.tijyoHeight  = result.tijyoHeight;
      item.rikakuHaiden = result.rikakuHaiden;
      item.rikakuUpper  = result.rikakuUpper;
      item.rikakuLower  = result.rikakuLower;
      view.commitEdit();
    }
  };

  /** 設備削除 */
  handleSetubiDelete = (setubi) => {
    const view = this.gridRef.current?.getView();
    if (view) {
      console.info('設備削除', setubi);

      // 設備一覧の該当項目を検索
      const item = view.sourceCollection.find(
          (source) => source.recordId === setubi.recordId);
      if (!item) {
        return;
      }

      // 設備一覧から削除
      view.remove(item);
    }
  };

  /** 設備選択 */
  handleSetubiSelect = (setubi) => {
    console.info('設備選択', setubi);

    // 新しい項目を作成して状態更新
    const setubiSelected = { ...setubi };
    this.setState({ setubiSelected });
  }

  /**
   * 設備一覧更新
   */
  handleSetubiUpdate = (newList) => {

    // 新しい一覧を作成して状態更新
    const setubiList = [ ...newList ];
    this.setState({ setubiList });
  };
}

// プロパティ
OrderSetubi.propTypes = {

  // ルーティング
  params: PropTypes.object,
  navigate: PropTypes.func,

  // ストア
  genericMap: PropTypes.object.isRequired,
  doShowMessage: PropTypes.func.isRequired,
}

// ルーティング
OrderSetubi = withRouter(OrderSetubi);

// ストア接続
const mapStateToProps = (state) => ({
  genericMap: state.master.genericMap,
});
const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};
OrderSetubi = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderSetubi);

export default OrderSetubi;
